import { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { secureAxiosInstance, secureAxiosInstanceWithErrorMessageInterceptor } from '../api-service';
import { configuration } from '../configuration';
import { getTenant } from '../tenant-support/get-tenant';
import { win } from '../utilities/window';

export interface BffSessionUser {
  id?: string;
  sub?: string;
  name?: string;
  tenant?: string;
  userProvisioningMessage?: string;
}

export interface BffSession {
  cookieSessionEnabled: boolean;
  user?: BffSessionUser;
}

// the BFF only reads its session cookie on calls that carry this header, which a forged cross-site request cannot
const csrfHeader = 'X-CSRF';
const legacySession: BffSession = { cookieSessionEnabled: false };

let cookieSession = false;

export function isCookieSession(): boolean {
  return cookieSession;
}

/**
 * Asks the BFF whether the tenant logs in through the BFF, and who is logged in if so. Anything but an answer keeps the
 * tenant on the legacy login, so a broken endpoint cannot lock anyone out.
 */
export async function fetchBffSession(): Promise<BffSession> {
  try {
    const response = await fetch(`${configuration.bffBase}/bff/session`, {
      credentials: 'include',
      headers: { 'X-Tenant': getTenant().name },
      signal: AbortSignal.timeout(10_000),
    });
    if (!response.ok) {
      return legacySession;
    }
    const session: BffSession = await response.json();
    cookieSession = session.cookieSessionEnabled;
    return session;
  } catch {
    return legacySession;
  }
}

export function bffLoginUrl(returnUrl: string, options: { identityProvider?: string; apexStsLogin?: boolean } = {}): string {
  const query = new URLSearchParams({ tenant: getTenant().name, returnUrl });
  if (options.identityProvider) {
    query.set('idp', options.identityProvider);
  }
  if (options.apexStsLogin) {
    query.set('useApexStsLogin', 'true');
  }
  return `${configuration.bffBase}/bff/login?${query}`;
}

export function bffLogoutUrl(): string {
  return `${configuration.bffBase}/bff/logout?${new URLSearchParams({ tenant: getTenant().name })}`;
}

export function currentUrl(): string {
  return win.location.href;
}

/** The headers a raw fetch to the BFF needs, for whichever login the tenant uses. */
export function authenticationHeaders(accessToken: string | undefined): Record<string, string> {
  if (cookieSession) {
    return { [csrfHeader]: '1' };
  }
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
}

export function authenticationCredentials(): RequestCredentials {
  return cookieSession ? 'include' : 'same-origin';
}

export function sendSessionCookie(requestConfig: InternalAxiosRequestConfig) {
  requestConfig.withCredentials = true;
  requestConfig.headers.set(csrfHeader, '1');
  return requestConfig;
}

// every open call fails with 401 at once when the session ends, and the caseworker is sent on only once
export function endSessionOnceOnUnauthorized(onSessionEnded: () => void) {
  let sessionEnded = false;
  return function endSessionOnUnauthorized(error: AxiosError) {
    if (error.response?.status === 401 && !sessionEnded) {
      sessionEnded = true;
      onSessionEnded();
    }
    return Promise.reject(error);
  };
}

/**
 * Every call to the BFF carries the session cookie, and a call the BFF answers with 401 means the session has ended, so
 * the caseworker is sent to log in again, or to the logged-out page when the session was logged out elsewhere.
 */
export function sendSessionCookieWithHttpRequests(onSessionEnded: () => void) {
  const endSessionOnUnauthorized = endSessionOnceOnUnauthorized(onSessionEnded);
  [secureAxiosInstance, secureAxiosInstanceWithErrorMessageInterceptor].forEach(instance => {
    instance.interceptors.request.use(sendSessionCookie);
    instance.interceptors.response.use(undefined, endSessionOnUnauthorized);
  });
}
